/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Header from '@/components/Header';
import ResizeWrapper from '@/components/ResizeWrapper';
import Menu from './Menu';
import EmotionWarnings from './Menu/components/EmotionWarnings';
import { useIsMobile } from '@/utils/utils';
import Apis from '@/app/index';
import MainArea from './MainArea';
import { ABORT_VISIBILITY_CHANGE, useLeave } from '@/lib/useCommon';
import { RTCConfig, SceneConfig, updateRTCConfig, updateScene, updateSceneConfig } from '@/store/slices/room';
import { RootState } from '@/store';
import styles from './index.module.less';

/**
 * MainPage 组件
 * 
 * SSE 连接流程：
 * 1. EmotionWarnings 组件放在 MainPage 层级，确保不受 Menu 渲染条件影响
 * 2. SSE 连接基于 isAuthenticated && isJoined 状态
 * 3. 当用户登录并加入对话后，自动创建 SSE 连接
 * 4. 当用户退出对话或退出登录时，自动关闭 SSE 连接
 */
export default function () {
  const leaveRoom = useLeave();
  const dispatch = useDispatch();
  const room = useSelector((state: RootState) => state.room);
  const auth = useSelector((state: RootState) => state.auth);
  const { isInChatPage, currentCardView } = room;
  const isMobileDevice = useIsMobile();
  
  // 判断是否显示 Menu：只在对话页面时显示（卡片视图页面不显示），且非移动端
  const shouldShowMenu = isInChatPage && !currentCardView && !isMobileDevice;

  // 调试日志：追踪关键状态变化
  useEffect(() => {
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #722ed1');
    console.log('%c🏠 [MainPage] 状态变化', 'color: #722ed1; font-weight: bold; font-size: 13px');
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #722ed1');
    console.log('isAuthenticated:', auth.isAuthenticated);
    console.log('isJoined:', room.isJoined);
    console.log('isInChatPage:', isInChatPage);
    console.log('currentCardView:', currentCardView);
    console.log('shouldShowMenu:', shouldShowMenu);
    console.log('isMobileDevice:', isMobileDevice);
    console.log('SSE 连接条件:', auth.isAuthenticated && room.isJoined ? '满足 ✅' : '不满足 ❌');
    console.log('时间:', new Date().toLocaleTimeString());
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #722ed1');
  }, [auth.isAuthenticated, room.isJoined, isInChatPage, currentCardView, shouldShowMenu, isMobileDevice]);

  const getScenes = async (username?: string) => {
    try {
      console.log('[getScenes] 获取场景配置, username:', username || '(未登录)');
      
      // 传递 username 参数到服务端
      const result = await Apis.Basic.getScenes({ username: username || '' });
      
      // 检查返回结果是否有效
      if (!result || !result.scenes || !Array.isArray(result.scenes) || result.scenes.length === 0) {
        console.warn('[getScenes] 返回结果为空或格式不正确:', result);
        return;
      }
      
      const { scenes }: {
        scenes: {
          rtc: RTCConfig;
          scene: SceneConfig;
        }[];
      } = result;
      
      console.log('[getScenes] 获取到场景配置:', scenes.length, '个场景');
      console.log('[getScenes] RTC 配置:', scenes[0].rtc);
      
      dispatch(updateScene(scenes[0].scene.id));
      dispatch(updateSceneConfig(
        scenes.reduce<Record<string, SceneConfig>>((prev, cur) => {
          prev[cur.scene.id] = cur.scene;
          return prev;
        }, {})
      ));
      dispatch(updateRTCConfig(
        scenes.reduce<Record<string, RTCConfig>>((prev, cur) => {
          prev[cur.scene.id] = cur.rtc;
          return prev;
        }, {})
      ));
    } catch (error) {
      console.error('[getScenes] 获取场景配置失败:', error);
    }
  };

  useEffect(() => {
    // 传递当前登录的用户名到 getScenes
    getScenes(auth.username);
    const isOriginalDemo = window.location.host.startsWith('localhost');
    const handler = () => {
      if (
        document.visibilityState === 'hidden' &&
        !sessionStorage.getItem(ABORT_VISIBILITY_CHANGE)
      ) {
        leaveRoom();
      }
    };
    !isOriginalDemo && document.addEventListener('visibilitychange', handler);
    return () => {
      !isOriginalDemo && document.removeEventListener('visibilitychange', handler);
    };
  }, []);

  return (
    <ResizeWrapper className={styles.container}>
      <Header />
      
      {/* 
        SSE 连接管理组件 
        - 放在 MainPage 层级，确保不受 Menu 渲染条件影响
        - 隐藏显示但保持运行
        - 内部根据 isAuthenticated && isJoined 状态自动管理连接
      */}
      {auth.isAuthenticated && (
        <div style={{ display: 'none' }}>
          <EmotionWarnings />
        </div>
      )}
      
      <div
        className={styles.main}
        style={{
          padding: isMobileDevice ? '' : '24px',
        }}
      >
        <div className={`${styles.mainArea} ${isMobileDevice ? styles.isMobile : ''} ${!shouldShowMenu ? styles.fullWidth : ''}`}>
          <MainArea />
        </div>
        {shouldShowMenu && (
          <div className={styles.operationArea}>
            <Menu />
          </div>
        )}
      </div>
    </ResizeWrapper>
  );
}
